import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import * as Sentry from '@sentry/react';

interface TokenRefreshConfig {
    onRefresh: (orgId: bigint | undefined) => Promise<string>;
    onTokenUpdate: (token: string) => Promise<void>;
    onExpired: () => void;
    getExpiration: () => Date | undefined;
}

function getAuthErrorType(error: unknown): string | undefined {
    if (!error || typeof error !== 'object') {
        return undefined;
    }
    if (!('type' in error)) {
        return undefined;
    }
    const type = error.type;
    if (typeof type === 'string') {
        return type.toLowerCase();
    }
    return undefined;
}

/**
 * Manages token refresh lifecycle with automatic scheduling and retry logic
 */
export class TokenRefreshManager {
    private static readonly MAX_RETRIES = 3;
    private static readonly INITIAL_RETRY_DELAY = 30000; // 30 seconds
    private static readonly MAX_RETRY_DELAY = 300000; // 5 minutes
    public static readonly REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes before expiration

    private refreshTimer?: NodeJS.Timeout;
    private expirationTimer?: NodeJS.Timeout;
    private retryCount = 0;
    private refreshPromise: Promise<void> | null = null;
    private config: TokenRefreshConfig;
    private orgId?: bigint;
    private isOnline = true;

    constructor(config: TokenRefreshConfig) {
        this.config = config;
    }

    /**
     * Initialize the manager with an organization ID
     */
    public init(orgId: bigint | undefined) {
        this.orgId = orgId;
        this.schedule();
    }

    /**
     * Update online status
     */
    public setOnlineStatus(isOnline: boolean) {
        this.isOnline = isOnline;
        if (isOnline) {
            this.schedule();
        } else {
            this.cancel();
        }
    }

    /**
     * Schedule the next token refresh
     */
    public schedule() {
        this.cancelRefreshTimer();
        this.scheduleExpirationCheck();

        if (!this.isOnline) {
            return;
        }

        const expiration = this.config.getExpiration();
        if (!expiration) {
            return;
        }

        const now = Date.now();
        const expirationTime = expiration.getTime();
        const refreshTime = Math.max(0, expirationTime - now - TokenRefreshManager.REFRESH_BUFFER_MS);

        this.refreshTimer = setTimeout(() => {
            this.refresh();
        }, refreshTime);
    }

    /**
     * Cancel all scheduled timers
     */
    public cancel() {
        this.cancelRefreshTimer();
        this.cancelExpirationTimer();
        this.retryCount = 0;
        this.refreshPromise = null;
    }

    /**
     * Force an immediate token refresh
     */
    public async forceRefresh(): Promise<void> {
        this.cancelRefreshTimer();
        await this.refresh();
    }

    /**
     * Clean up resources
     */
    public destroy() {
        this.cancel();
    }

    /**
     * Perform token refresh with retry logic
     */
    private async refresh(): Promise<void> {
        // If already refreshing, wait for the existing refresh to complete
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        // Create a new refresh promise
        this.refreshPromise = this.performRefresh();

        try {
            await this.refreshPromise;
        } finally {
            // Clear the promise when done (success or failure)
            this.refreshPromise = null;
        }
    }

    /**
     * Actually perform the refresh operation
     */
    private async performRefresh(): Promise<void> {
        try {
            if (import.meta.env.DEV) {
                console.log('[TokenRefresh] Attempting to refresh authentication token...');
            }

            const newToken = await this.config.onRefresh(this.orgId);
            await this.config.onTokenUpdate(newToken);

            // Reset retry count on success
            const hadRetries = this.retryCount > 0;
            this.retryCount = 0;

            if (import.meta.env.DEV) {
                if (hadRetries) {
                    console.log('[TokenRefresh] Token refresh successful after retries.');
                } else {
                    console.log('[TokenRefresh] Token refresh successful.');
                }
            }

            // Schedule next refresh
            this.schedule();
        } catch (error) {
            // Handle the error but don't re-throw if we're going to retry
            this.handleRefreshError(error);
        }
    }

    /**
     * Handle token refresh errors with exponential backoff
     */
    private handleRefreshError(error: unknown) {
        // Determine error type for better logging and handling
        const errorType = this.categorizeError(error);
        const isNetworkError = errorType === 'network';
        const isAuthError = errorType === 'auth';

        // FAST FAIL: Non-recoverable errors should not be retried
        // These errors indicate the refresh token is invalid and retrying will never succeed
        if (this.isNonRecoverableError(error)) {
            console.warn('[TokenRefresh] Non-recoverable auth error detected. Redirecting immediately.', { errorType });

            this.config.onExpired();
            return; // Skip all retry logic
        }

        if (this.retryCount < TokenRefreshManager.MAX_RETRIES) {
            const backoffDelay = this.calculateBackoffDelay();
            this.retryCount++;

            // Log to console for debugging (visible in dev tools)
            // Don't show toast messages for retries to avoid annoying users
            if (isNetworkError) {
                console.warn(
                    `[TokenRefresh] Network error during token refresh (attempt ${this.retryCount}/${TokenRefreshManager.MAX_RETRIES}). Retrying in ${Math.round(backoffDelay / 1000)} seconds...`,
                );
            } else if (isAuthError) {
                console.warn(
                    `[TokenRefresh] Authentication error during token refresh (attempt ${this.retryCount}/${TokenRefreshManager.MAX_RETRIES}). Retrying in ${Math.round(backoffDelay / 1000)} seconds...`,
                );
            } else {
                console.warn(
                    `[TokenRefresh] Unknown error during token refresh (attempt ${this.retryCount}/${TokenRefreshManager.MAX_RETRIES}). Retrying in ${Math.round(backoffDelay / 1000)} seconds...`,
                    error,
                );
            }

            Sentry.addBreadcrumb({
                category: 'auth.token_refresh',
                level: isNetworkError ? 'info' : 'warning',
                message: 'Token refresh retry scheduled',
                data: {
                    retry_count: this.retryCount,
                    error_type: errorType,
                    backoff_delay: backoffDelay,
                    is_online: this.isOnline,
                },
            });

            // Retry with exponential backoff
            this.refreshTimer = setTimeout(() => {
                this.refresh();
            }, backoffDelay);
        } else {
            // Max retries reached - show user-facing error message
            console.error(
                '[TokenRefresh] Max retries reached for token refresh. Session will be marked as expired.',
                error,
            );
            errorMessage.show('Unable to refresh session. Please login again.');
            this.config.onExpired();

            Sentry.captureException(error, {
                tags: {
                    location: 'TokenRefreshManager',
                    location_details: 'token_refresh_max_retries',
                    error_type: errorType,
                },
                extra: {
                    total_retries: this.retryCount,
                    is_online: this.isOnline,
                },
            });
        }
    }

    /**
     * Categorize errors for better handling
     */
    private categorizeError(error: unknown): 'network' | 'auth' | 'unknown' {
        if (!error) return 'unknown';

        const authErrorType = getAuthErrorType(error);
        if (
            authErrorType &&
            ['token_expired', 'invalid_grant', 'missing_refresh_token', 'login_required'].includes(authErrorType)
        ) {
            return 'auth';
        }

        const errorText = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();

        // Network errors
        if (
            errorText.includes('network') ||
            errorText.includes('fetch') ||
            errorText.includes('timeout') ||
            errorText.includes('connection')
        ) {
            return 'network';
        }

        // Auth errors
        if (
            errorText.includes('unauthorized') ||
            errorText.includes('auth') ||
            errorText.includes('login_required') ||
            errorText.includes('invalid_grant') ||
            errorText.includes('jwt expired') ||
            errorText.includes('token_expired')
        ) {
            return 'auth';
        }

        return 'unknown';
    }

    /**
     * Check if an error is non-recoverable and should not be retried.
     * These errors indicate the refresh token is invalid and retrying will never succeed.
     */
    private isNonRecoverableError(error: unknown): boolean {
        if (!error) return false;

        const errorText = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();

        // Check error object for Auth0 error code
        if (error && typeof error === 'object' && 'error' in error) {
            const errorCode = (error as { error?: string }).error?.toLowerCase();
            if (
                errorCode === 'invalid_grant' ||
                errorCode === 'missing_refresh_token' ||
                errorCode === 'login_required' ||
                errorCode === 'consent_required'
            ) {
                return true;
            }
        }

        const authErrorType = getAuthErrorType(error);
        if (
            authErrorType &&
            ['token_expired', 'invalid_grant', 'missing_refresh_token', 'login_required'].includes(authErrorType)
        ) {
            return true;
        }

        // Check message content for non-recoverable patterns
        const nonRecoverablePatterns = [
            'invalid_grant',
            'invalid grant',
            'missing_refresh_token',
            'unknown or invalid refresh token',
            'login_required',
            'jwt expired',
            'token_expired',
        ];

        return nonRecoverablePatterns.some((pattern) => errorText.includes(pattern));
    }

    /**
     * Calculate exponential backoff delay
     */
    private calculateBackoffDelay(): number {
        return Math.min(
            TokenRefreshManager.INITIAL_RETRY_DELAY * Math.pow(2, this.retryCount),
            TokenRefreshManager.MAX_RETRY_DELAY,
        );
    }

    /**
     * Schedule expiration check
     */
    private scheduleExpirationCheck() {
        this.cancelExpirationTimer();

        const expiration = this.config.getExpiration();
        if (!expiration) {
            return;
        }

        const now = Date.now();
        const expirationTime = expiration.getTime();
        const timeUntilExpiration = expirationTime - now;

        if (timeUntilExpiration <= 0) {
            // Token already expired
            this.config.onExpired();
        } else {
            // Schedule check for when token expires
            this.expirationTimer = setTimeout(() => {
                this.config.onExpired();
            }, timeUntilExpiration);
        }
    }

    /**
     * Cancel refresh timer
     */
    private cancelRefreshTimer() {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = undefined;
        }
    }

    /**
     * Cancel expiration timer
     */
    private cancelExpirationTimer() {
        if (this.expirationTimer) {
            clearTimeout(this.expirationTimer);
            this.expirationTimer = undefined;
        }
    }

    /**
     * Get current status
     */
    public getStatus() {
        return {
            isRefreshing: !!this.refreshPromise,
            retryCount: this.retryCount,
            isOnline: this.isOnline,
            hasScheduledRefresh: !!this.refreshTimer,
            nextRefreshIn: this.getNextRefreshTime(),
        };
    }

    /**
     * Get time until next refresh in milliseconds
     */
    private getNextRefreshTime(): number | null {
        const expiration = this.config.getExpiration();
        if (!expiration) {
            return null;
        }

        const now = Date.now();
        const expirationTime = expiration.getTime();
        return Math.max(0, expirationTime - now - TokenRefreshManager.REFRESH_BUFFER_MS);
    }
}
